import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import {
  DashboardLocale,
  DashboardMessageKey,
  formatDashboardMessage,
} from './dashboardMessages';
import { setHardcodedUiLocale } from './hardcodedKoEn';

type DashboardT = (
  key: DashboardMessageKey,
  params?: Record<string, string | number>,
) => string;

type DashboardLocaleContextValue = {
  locale: DashboardLocale;
  t: DashboardT;
};

const DashboardLocaleContext = createContext<DashboardLocaleContextValue | null>(null);

export function DashboardLocaleProvider({
  locale,
  children,
}: {
  locale: DashboardLocale;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = locale;
    setHardcodedUiLocale(locale);
    return () => {
      document.documentElement.lang = prev;
    };
  }, [locale]);

  const t = useCallback<DashboardT>(
    (key, params) => formatDashboardMessage(locale, key, params),
    [locale],
  );

  const value = useMemo(() => ({ locale, t }), [locale, t]);

  return (
    <DashboardLocaleContext.Provider value={value}>
      {children}
    </DashboardLocaleContext.Provider>
  );
}

export function useDashboardLocale(): DashboardLocaleContextValue {
  const ctx = useContext(DashboardLocaleContext);
  if (!ctx) {
    throw new Error('useDashboardLocale must be used within DashboardLocaleProvider');
  }
  return ctx;
}

export function useDashboardT(): DashboardT {
  const { locale } = useDashboardLocale();
  return useCallback(
    (key, params) => formatDashboardMessage(locale, key, params),
    [locale],
  );
}
